import { ExternalLink } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { keys } from "../api/queryKeys.ts";
import { api } from "../api/client.ts";
import CountryName from "../components/CountryName.tsx";
import DetailRow from "../components/DetailRow.tsx";
import FormattedDate from "../components/FormattedDate.tsx";
import SectionCard from "../components/SectionCard.tsx";
import DescriptionBlock from "../components/detail/DescriptionBlock.tsx";
import DetailHeader from "../components/detail/DetailHeader.tsx";
import DetailPageStatus from "../components/detail/DetailPageStatus.tsx";
import { humanize } from "../utils/format.ts";
import { sentimentColor } from "../utils/statusColors.ts";

const BACK = { to: "/news-articles", label: "News articles" };

type SummarySentence = string | { sentence: string | { sentence: string } };

function sentenceText(s: SummarySentence): string {
  if (typeof s === "string") return s;
  if (typeof s.sentence === "string") return s.sentence;
  return s.sentence.sentence ?? "";
}

/** A labelled row of chips (keywords, categories, topics); nothing when empty. */
function ChipGroup({ label, children }: { label: string; children: ReactNode[] }) {
  if (children.length === 0) return null;
  return (
    <Box sx={{ mb: 1.5, "&:last-child": { mb: 0 } }}>
      <Typography variant="caption" component="div" sx={{ color: "text.secondary", mb: 0.5 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }} useFlexGap>
        {children}
      </Stack>
    </Box>
  );
}

function NewsArticleDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: keys.newsArticle.detail(id),
    queryFn: () => api.getNewsArticle(id!),
    enabled: !!id,
  });

  if (isLoading || error || !data) {
    return (
      <DetailPageStatus
        isLoading={isLoading}
        message={error ? `Error loading article: ${(error as Error).message}` : "Article not found"}
        back={BACK}
      />
    );
  }

  const summary = Array.isArray(data.summary) ? (data.summary as SummarySentence[]) : [];
  const keywords = data.keywords ?? [];
  const categories = data.categories ?? [];
  const topics = data.topics ?? [];

  return (
    <>
      <DetailHeader
        back={BACK}
        title={
          data.title ?? (
            <Box component="em" sx={{ color: "text.disabled" }}>
              (no title)
            </Box>
          )
        }
        sourceUrl={null}
        actions={
          data.document_url && (
            <Button
              component="a"
              href={data.document_url}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              endIcon={<ExternalLink fontSize="small" />}
            >
              Open source
            </Button>
          )
        }
        chips={
          <>
            <Chip size="small" label={data.source} />
            {data.sentiment_overall != null && (
              <Chip
                size="small"
                label={`Sentiment ${data.sentiment_overall.toFixed(2)}${
                  data.sentiment_polarity ? ` (${data.sentiment_polarity})` : ""
                }`}
                color={sentimentColor(data.sentiment_overall)}
              />
            )}
            {data.is_breaking && <Chip size="small" label="Breaking" color="warning" />}
            {data.is_duplicate && <Chip size="small" label="Duplicate" variant="outlined" />}
            {data.source_country && (
              <Chip size="small" label={<CountryName code={data.source_country} />} variant="outlined" />
            )}
          </>
        }
      />

      <SectionCard title="Details">
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <DetailRow label="Publication" value={data.source_domain ?? data.source_common_name} />
            <DetailRow label="Author" value={data.author_name} />
            <DetailRow label="Language" value={data.language} />
            <DetailRow
              label="Read time"
              value={data.read_time_minutes != null ? `${data.read_time_minutes} min` : null}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <DetailRow
              label="Published"
              value={data.published_at ? <FormattedDate value={data.published_at} /> : null}
            />
            <DetailRow label="Ingested" value={<FormattedDate value={data.created_at} />} />
            <DetailRow label="Extraction status" value={humanize(data.extraction_status)} />
          </Grid>
        </Grid>
      </SectionCard>

      {(data.image_url || data.description || summary.length > 0) && (
        <SectionCard title="Summary">
          {data.image_url && (
            <Box
              component="img"
              src={data.image_url}
              alt={data.title ?? "Article image"}
              sx={{ display: "block", maxWidth: "100%", maxHeight: 320, borderRadius: 1, mb: 1.5 }}
            />
          )}
          {data.description && (
            <Typography
              variant="body1"
              sx={{ fontStyle: "italic", color: "text.secondary", mb: summary.length > 0 ? 1.5 : 0 }}
            >
              {data.description}
            </Typography>
          )}
          {summary.length > 0 && (
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {summary.map((s, i) => (
                <Typography key={i} component="li" variant="body2">
                  {sentenceText(s)}
                </Typography>
              ))}
            </Box>
          )}
        </SectionCard>
      )}

      {data.body && (
        <SectionCard title="Article body">
          <DescriptionBlock>{data.body}</DescriptionBlock>
        </SectionCard>
      )}

      {(keywords.length > 0 || categories.length > 0 || topics.length > 0) && (
        <SectionCard title="Tags">
          <ChipGroup label="Keywords">
            {keywords.map((t) => (
              <Chip key={t} size="small" label={t} />
            ))}
          </ChipGroup>
          <ChipGroup label="Categories">
            {categories.map((c) => (
              <Chip key={String(c.id)} size="small" label={c.name} variant="outlined" />
            ))}
          </ChipGroup>
          <ChipGroup label="Topics">
            {topics.map((t) => (
              <Chip key={String(t.id)} size="small" label={t.name} variant="outlined" />
            ))}
          </ChipGroup>
        </SectionCard>
      )}
    </>
  );
}

export default NewsArticleDetailPage;
